import { Box, Skeleton } from "@mui/material";

type HomeTableSkeletonProps = {
  /** Number of column placeholders in the table. */
  columns?: number;
  /** Number of body row placeholders. */
  rows?: number;
  /** Render the tab strip above the table (screenshot 1). Set false on pages
   *  that already render real tabs above the loading area. */
  showTabs?: boolean;
  /** Number of tab placeholders when showTabs is true. */
  tabCount?: number;
  /** Render a pagination placeholder below the table. */
  showPagination?: boolean;
};

const HEADER_BG = "#F4F5F7";
const BORDER = "custom.greyish.7";
const ROW_BORDER = "custom.greyish.1";
const MIN_COL_WIDTH = 130;

const HomeTableSkeleton = ({
  columns = 9,
  rows = 8,
  showTabs = true,
  tabCount = 8,
  showPagination = true,
}: HomeTableSkeletonProps) => {
  const columnArray = Array.from({ length: columns });
  const rowArray = Array.from({ length: rows });
  const tabArray = Array.from({ length: tabCount });

  // Keep every column the same width so header and body stay aligned; the
  // whole table scrolls horizontally like the real one when space is tight.
  const cellSx = { flex: `1 0 ${MIN_COL_WIDTH}px`, minWidth: MIN_COL_WIDTH };

  const renderBodyCell = (colIndex: number) => {
    // First column = S.No (short), second = REG.NO pill, rest = text.
    if (colIndex === 0)
      return <Skeleton variant="text" animation="wave" width={24} height={20} />;
    if (colIndex === 1)
      return (
        <Skeleton
          variant="rounded"
          animation="wave"
          width={48}
          height={24}
          sx={{ borderRadius: "12px" }}
        />
      );
    return (
      <Skeleton
        variant="text"
        animation="wave"
        height={20}
        sx={{ width: colIndex % 2 ? "55%" : "75%" }}
      />
    );
  };

  return (
    <Box aria-busy="true" sx={{ width: "100%", background: "#fff" }}>
      {showTabs && (
        <Box
          sx={{
            display: "flex",
            gap: "28px",
            px: "16px",
            borderBottom: "1px solid",
            borderColor: BORDER,
            overflowX: "auto",
          }}
        >
          {tabArray.map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              animation="wave"
              height={22}
              sx={{ my: "16px", minWidth: 70, flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: columns * MIN_COL_WIDTH }}>
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              gap: "24px",
              px: "20px",
              py: "16px",
              background: HEADER_BG,
            }}
          >
            {columnArray.map((_, index) => (
              <Box key={index} sx={cellSx}>
                <Skeleton
                  variant="text"
                  animation="wave"
                  height={18}
                  sx={{ width: "60%" }}
                />
              </Box>
            ))}
          </Box>

          {/* Body rows */}
          {rowArray.map((_, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
                px: "20px",
                py: "18px",
                borderBottom: "1px solid",
                borderColor: ROW_BORDER,
              }}
            >
              {columnArray.map((_, colIndex) => (
                <Box key={colIndex} sx={cellSx}>
                  {renderBodyCell(colIndex)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {showPagination && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            py: "16px",
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="circular"
              animation="wave"
              width={22}
              height={22}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HomeTableSkeleton;
